/**
 * Query key factory for dashboard queries. Shared by all dashboard query and
 * mutation hooks so the read and write sides cannot drift.
 *
 * `orgId` is hoisted to a fixed position immediately after each resource root
 * (dashboards never cross org boundaries), following the same convention as
 * `metricsQueryKeys`. On the resource-root accessors (`lists`, `ids`) an
 * undefined `orgId` is omitted rather than encoded as a literal `undefined`:
 * that keeps the org-agnostic root a genuine prefix of the org-scoped keys, so
 * a broad invalidation still partial-matches the narrower ones. (A
 * trailing/embedded `undefined` is a real array element and would break
 * partial-match invalidation.)
 *
 * The list and single-dashboard keys live under distinct top-level namespaces
 * ("dashboards" vs "dashboard") so invalidating the list does not touch
 * single-dashboard caches (and vice versa).
 *
 * `id` embeds its own discriminator (`dashboardId`) *after* `orgId`, so it
 * cannot use the omit trick — dropping an undefined `orgId` would slide the
 * dashboardId into the org slot. It therefore requires `orgId`; a write always
 * knows the org it targets, and reads are disabled until the org is known.
 *
 * @see https://tkdodo.eu/blog/effective-react-query-keys#use-query-key-factories
 */
export const dashboardQueryKeys = {
  // --- Dashboard lists ---
  lists: (orgId?: string) =>
    orgId === undefined
      ? (["dashboards"] as const)
      : (["dashboards", orgId] as const),
  list: (orgId: string | undefined) =>
    [...dashboardQueryKeys.lists(orgId), "list"] as const,

  // --- Single dashboards ---
  ids: (orgId?: string) =>
    orgId === undefined
      ? (["dashboard"] as const)
      : (["dashboard", orgId] as const),
  id: (dashboardId: string, orgId: string) =>
    [...dashboardQueryKeys.ids(orgId), dashboardId] as const,
};
